"use client";
import {
  AGE_OPTIONS as DEFAULT_AGE_OPTIONS,
  datePickerFormatJP,
  DEFAULT_ADULTS,
  DEFAULT_CHECKIN_DATE,
  DEFAULT_CHECKOUT_DATE,
} from "@/constants";
import { TRIPLA_BOOKING_URL } from "@/constants/enviroment";
import { useModal } from "@/hooks/useModal/useModal";
import { DEFAULT_LOCALE, LOCALE_BASE_MAP } from "@/i18n/routing";
import { cn } from "@/lib/utils";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import useSearchFormStore, {
  FilterRoomModalStateEnum,
} from "@/store/searchFormStore/searchFormStore";
import { ERoomOrderBy, ERoomType } from "@/types/roomsList";
import {
  checkAndValidateStoredDates,
  formatDateForStorage,
  generateListAge,
  groupJapanAreas,
  parseStoredDate,
  redirectBookingCard,
  removeTrailingSlashPathName,
  cityAndHotelFuzzyFilter,
  SelectOption as UtilSelectOption,
} from "@/utils";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { CircleChevronRight, MapPin, Phone } from "lucide-react";
import * as React from "react";
import { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { RoomSelect } from "./RoomSelect";
import {
  CustomSearchAvailableProps,
  schemaSearchAvailableRoom,
  SearchAvailableRoom,
  SearchAvailableRoom as SearchAvailableRoomType,
} from "./SearchAvailableRoomMobile";

import { iconList } from "@/assets/icons";
import { getSearchRoomData } from "@/components/organisms/hotel-result/filter/utils";
import { JapanHotel } from "@/types/generated";
import { format } from "date-fns/format";
import { useParams, usePathname } from "next/navigation";
import { GroupBase } from "react-select";
import {
  CONFIG_SEARCH_AVAILABLE_ROOM,
  createBookingParams,
  deepCloneRoom,
  TRoom,
} from "./ultils";
import { Link } from "@/components/atoms/link";
import { CustomSelect } from "@/components/atoms/dropdown";
import { Button } from "@/components/atoms/button";
import { Modal } from "@/components/atoms/modal";
import { DateRangePicker } from "@/components/atoms/date-range-picker";
import { Input } from "@/components/atoms/input";
import { useTriplaOTT } from "@/hooks/useTripla/useTriplaOTT";
import { useLocale } from "next-intl";
// import { getNewTriplaAccessToken } from "@app/[locale]/actions";

export type HotelOrCityType = UtilSelectOption & {
  label: string;
  value: string;
  bookingId?: string;
  hotelCode?: string;
  type?: string;
  city?: string;
  hotelItemName?: string;
  initial_adult_count?: string;
};

const Loading: React.FC = () => {
  const { Spinner } = iconList;
  return (
    <Spinner
      className="m-auto mt-5 h-10 w-10 animate-spin text-gray-200 dark:text-gray-600"
      fill={"var(--fill-gray-stronger)"}
    />
  );
};

const SearchAvailableRoomForm: React.FC<
  Omit<CustomSearchAvailableProps, "listOption"> & {
    isTopPage?: boolean;
    hotelOrCity?: HotelOrCityType | null;
    hotelId?: string;
    promoCode?: string;
    initial_adult_count?: string;
    forceLocaleSepcificData?: boolean;
  }
> = ({
  className,
  phone,
  timings,
  isTopPage = false,
  hotelOrCity,
  hotelId,
  isHiddenMobile,
  promoCode,
  initial_adult_count,
  forceLocaleSepcificData = false,
  ...props
}) => {
  const [showValidationError, setShowValidationError] = useState(false);
  const pathName = usePathname();
  const params = useParams();
  const {
    japanAreas,
    loading,
    allLocaleHotels,
    locale: japanAreasLocale,
    fetchAreas,
  } = useJapanAreasStore();
  const {
    configurations: searchAvailableRoom,
    configurationsLocale,
    fetchConfigurationsData,
    setFilterRoomModalState,
  } = useSearchFormStore();
  const { oneTimeToken } = useTriplaOTT();
  const searchRoomData = getSearchRoomData(searchAvailableRoom, params);
  const {
    configurations,
    rooms_configurations,
    booking,
    ages_configurations,
    tripla_section,
  } = searchRoomData || {};

  const {
    checkin_label,
    checkout_label,
    promo_code_label,
    promo_code_placeholder,
  } = configurations ?? {};
  const {
    adult_label,
    children_label,
    maximum_adults,
    maximum_child_age,
    maximum_children,
    maximum_rooms,
    single_room_label,
    default_adult_count,
  } = rooms_configurations ?? {};
  const {
    tripla_add_room_label,
    tripla_age_label,
    tripla_apply_label,
    tripla_bed_label,
    tripla_child_label,
    tripla_room_adults_label,
    tripla_room_children_label,
    tripla_room_name_label,
    tripla_with_bed_label,
    tripla_without_bed_label,
    tripla_age_placeholder,
    tripla_invalid_age_error,
  } = tripla_section ?? {};
  const { booking_button_text } = booking ?? {};

  const { age_placeholder } = ages_configurations ?? {};

  const [isLoading, setIsLoading] = useState(false);
  const [range, setRange] = useState<DateRange>({
    from: new Date(DEFAULT_CHECKIN_DATE),
    to: new Date(DEFAULT_CHECKOUT_DATE),
  });
  const [inputValue, setInputValue] = useState<string>("");
  const [previousRange, setPreviousRange] = useState<DateRange>({
    from: new Date(DEFAULT_CHECKIN_DATE),
    to: new Date(DEFAULT_CHECKOUT_DATE),
  });
  const { setFormData, formData } = useSearchFormStore();
  const pathArr = removeTrailingSlashPathName(pathName)?.split("/") || [];
  const hotel_slug = pathArr?.[(pathArr?.length ?? 0) - 1];

  const MAXIMUM_ADULTS_LIMIT = maximum_adults
    ? Number(maximum_adults)
    : CONFIG_SEARCH_AVAILABLE_ROOM.MAXIMUM_ADULTS_LIMIT;
  const MAXIMUM_CHILDREN_LIMIT = maximum_children
    ? Number(maximum_children)
    : CONFIG_SEARCH_AVAILABLE_ROOM.MAXIMUM_CHILDREN_LIMIT;
  const MAXIMUM_ROOMS_LIMIT = maximum_rooms
    ? Number(maximum_rooms)
    : CONFIG_SEARCH_AVAILABLE_ROOM.MAXIMUM_ROOMS_LIMIT;
  const AGE_OPTIONS =
    generateListAge(
      Number(maximum_child_age),
      tripla_age_placeholder || undefined,
    ) || DEFAULT_AGE_OPTIONS;

  const handleRangeSelect = (
    nextRange: DateRange | undefined,
    selectedDay: Date | undefined,
  ) => {
    if (range.from && range.to) {
      setPreviousRange({ from: range.from, to: range.to });
      setFormData({
        ...formData,
        checkIn: formatDateForStorage(range.from, DEFAULT_CHECKIN_DATE),
        checkout: formatDateForStorage(range.to, DEFAULT_CHECKOUT_DATE),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    }
    if (nextRange) {
      setRange((currentRange) => {
        if (currentRange.from && currentRange.to) {
          return { from: selectedDay };
        }
        return nextRange;
      });
      if (nextRange.from) {
        setValue("checkIn", nextRange.from);
      }
      if (nextRange.to) {
        setValue("checkout", nextRange.to);
      }
    }
    if (!selectedDay) {
      setRange(
        nextRange || {
          from: new Date(DEFAULT_CHECKIN_DATE),
          to: new Date(DEFAULT_CHECKOUT_DATE),
        },
      );
    }
    setFilterRoomModalState(FilterRoomModalStateEnum.SUBMITED);
  };

  const listCityAndHotel = React.useMemo(
    () =>
      groupJapanAreas(
        japanAreas,
        allLocaleHotels as Record<string, JapanHotel[]> | null | undefined,
        undefined,
        "エリア",
        "ホテル",
      ),
    [allLocaleHotels, japanAreas],
  );

  const selectedHotelOrCity = React.useMemo(() => {
    if (!hotelId) return hotelOrCity ?? null;

    for (const group of listCityAndHotel) {
      if (group.options) {
        const found = group.options.find((opt) => opt.value === hotelId);
        if (found) return found;
      }
    }
    return hotelOrCity ?? null;
  }, [hotelId, hotelOrCity, listCityAndHotel]);

  const initialFormData = React.useMemo(() => {
    const { checkIn, checkOut } = checkAndValidateStoredDates(
      formData?.checkIn,
      DEFAULT_CHECKIN_DATE,
      formData?.checkout,
      DEFAULT_CHECKOUT_DATE,
    );
    return {
      hotelOrCity: formData?.hotelOrCity || selectedHotelOrCity,
      checkIn: parseStoredDate(checkIn, DEFAULT_CHECKIN_DATE),
      checkout: parseStoredDate(checkOut, DEFAULT_CHECKOUT_DATE),
      adults:
        Number(initial_adult_count) ||
        Number(default_adult_count) ||
        formData?.rooms?.[0]?.adults ||
        DEFAULT_ADULTS,
      promoCode: formData?.promoCode ? formData?.promoCode : promoCode || "",
      rooms:
        formData?.rooms && formData?.rooms?.length > 0
          ? formData.rooms
          : [
              {
                adults:
                  Number(initial_adult_count) ||
                  Number(default_adult_count) ||
                  Number(DEFAULT_ADULTS),
                children: [],
              },
            ],
    };
  }, [
    isTopPage,
    formData,
    default_adult_count,
    initial_adult_count,
    promoCode,
  ]);
  useEffect(() => {
    const { checkIn, checkOut } = checkAndValidateStoredDates(
      formData?.checkIn,
      DEFAULT_CHECKIN_DATE,
      formData?.checkout,
      DEFAULT_CHECKOUT_DATE,
    );
    setRange({
      from: parseStoredDate(checkIn, DEFAULT_CHECKIN_DATE),
      to: parseStoredDate(checkOut, DEFAULT_CHECKOUT_DATE),
    });
  }, []);
  const methods = useForm<SearchAvailableRoomType>({
    defaultValues: initialFormData,
    resolver: valibotResolver(schemaSearchAvailableRoom),
  });

  const {
    handleSubmit,
    control,
    formState,
    watch,
    setValue,
    reset,
    getValues,
  } = methods;
  const rooms = watch("rooms");
  const locale = useLocale();
  const localeFetchArgs = React.useMemo<
    [boolean | undefined, string | undefined]
  >(
    () =>
      forceLocaleSepcificData ? [false, locale] : [undefined, undefined],
    [forceLocaleSepcificData, locale],
  );
  const targetLocale = forceLocaleSepcificData ? locale : DEFAULT_LOCALE;

  useEffect(() => {
    if (configurationsLocale !== targetLocale) {
      fetchConfigurationsData(targetLocale, ...localeFetchArgs);
    }
  }, [
    configurationsLocale,
    fetchConfigurationsData,
    localeFetchArgs,
    targetLocale,
  ]);

  useEffect(() => {
    if (japanAreasLocale !== targetLocale || japanAreas.length === 0) {
      fetchAreas(targetLocale, false, ...localeFetchArgs);
    }
  }, [
    fetchAreas,
    japanAreas.length,
    japanAreasLocale,
    localeFetchArgs,
    targetLocale,
  ]);

  const adultsCount =
    rooms?.[0]?.adults ||
    Number(initial_adult_count) ||
    Number(default_adult_count) ||
    Number(DEFAULT_ADULTS);
  const childrenCount = rooms?.[0]?.children?.length || 0;
  const { isOpen, toggleModal } = useModal();
  const canIncreaseAdults = adultsCount < MAXIMUM_ADULTS_LIMIT;
  const canDecreaseAdults = adultsCount > 1;
  const canIncreaseChildren = childrenCount < MAXIMUM_CHILDREN_LIMIT;
  const canDecreaseChildren = childrenCount > 0;

  const incrementAdults = () => {
    if (rooms?.[0] && canIncreaseAdults) {
      const newRooms = deepCloneRoom([...(rooms as TRoom[])]);
      if (!newRooms[0]) newRooms[0] = { adults: 0, children: [] };
      newRooms[0] = { ...newRooms[0], adults: (newRooms[0].adults || 0) + 1 };
      setValue("rooms", newRooms);
      setFormData({ ...(formData as SearchAvailableRoom), rooms: newRooms });
      setFilterRoomModalState(FilterRoomModalStateEnum.SUBMITED);
    }
  };

  const decrementAdults = () => {
    if (rooms?.[0] && canDecreaseAdults) {
      const newRooms = deepCloneRoom([...(rooms as TRoom[])]);

      if (newRooms[0]) {
        // Extract assignment as required by SonarQube
        newRooms[0].adults -= 1;

        newRooms[0] = {
          adults: newRooms[0].adults,
          children: newRooms[0].children,
        };
      }

      setValue("rooms", newRooms);
      setFormData({ ...(formData as SearchAvailableRoom), rooms: newRooms });
      setFilterRoomModalState(FilterRoomModalStateEnum.SUBMITED);
    }
  };

  const incrementChildren = () => {
    if (rooms?.[0] && canIncreaseChildren) {
      const updatedRoomsForIncrement = deepCloneRoom([...(rooms as TRoom[])]);

      let firstRoom = updatedRoomsForIncrement[0];

      if (!firstRoom) {
        firstRoom = { adults: Number(DEFAULT_ADULTS), children: [] };
        updatedRoomsForIncrement[0] = firstRoom;
      }

      const newChildrenArray = [...(firstRoom.children || [])];
      newChildrenArray.push({ age: AGE_OPTIONS[0], isBed: false });
      firstRoom.children = newChildrenArray;

      setValue("rooms", updatedRoomsForIncrement);
      setFormData({
        ...(formData as SearchAvailableRoom),
        rooms: updatedRoomsForIncrement,
      });
      setFilterRoomModalState(FilterRoomModalStateEnum.SUBMITED);
      toggleModal();
    }
  };

  const decrementChildren = () => {
    if (rooms?.[0] && canDecreaseChildren) {
      const updatedRoomsForDecrement = deepCloneRoom([...(rooms as TRoom[])]);

      const firstRoom = updatedRoomsForDecrement[0];

      if (firstRoom?.children && firstRoom?.children.length > 0) {
        const newChildrenArray = [...firstRoom.children];
        newChildrenArray.pop();
        firstRoom.children = newChildrenArray;
      }

      setValue("rooms", updatedRoomsForDecrement);
      setFormData({
        ...(formData as SearchAvailableRoom),
        rooms: updatedRoomsForDecrement,
      });
      setFilterRoomModalState(FilterRoomModalStateEnum.SUBMITED);
    }
  };
  const onSubmit: SubmitHandler<SearchAvailableRoomType> = async (
    values: SearchAvailableRoomType,
  ) => {
    try {
      // setIsLoading(true);
      setIsLoading(true);
      setShowValidationError(false);
      if (!values.hotelOrCity) {
        setShowValidationError(true);
        return;
      }

      setFormData(values);

      if (values.hotelOrCity?.type === "city") {
        const slug_hotel =
          (japanAreas || [])?.find(
            (item) =>
              item?.area_name?.toLowerCase() ===
              values?.hotelOrCity?.label.toLowerCase(),
          )?.slug_hotel || "";
        const cityListRoute = `${globalThis.location.origin}/${locale}/${slug_hotel}`;
        window.open(cityListRoute, "_blank");
      } else {
        const response = await fetch("/api/tripla/access-token");

        const tokenData = (await response?.json()) ?? {};
        const oneTimeToken = tokenData?.one_time_access_token ?? "";

        const queryParams = createBookingParams(values, {
          type: ERoomType.ROOMS,
          is_day_use: false,
          order: ERoomOrderBy.RECOMMENDED,
          is_including_occupied: false,
          locale: LOCALE_BASE_MAP[locale],
          coupon_code: values?.promoCode || "",
          ...(oneTimeToken && { one_time_access_token: oneTimeToken }),
        });

        const url = values.hotelOrCity?.externalUrl || values.hotelOrCity?.slug;
        const useTripla = values.hotelOrCity?.useTripla;
        const hasTriplaBooking =
          TRIPLA_BOOKING_URL?.length && queryParams?.code?.length;

        if (useTripla && hasTriplaBooking) {
          redirectBookingCard(
            {
              ...queryParams,
              checkin: queryParams?.checkin || DEFAULT_CHECKIN_DATE,
              checkout: queryParams?.checkout || DEFAULT_CHECKOUT_DATE,
              children: queryParams?.children,
              kids_ages: queryParams?.kids_ages,
              locale: LOCALE_BASE_MAP[locale],
            },
            false,
          );
        } else if (url) {
          window.open(url, "_blank");
        }
      }
    } catch {
      console.log("error while searching available rooms");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const listHotel = listCityAndHotel.find((item) => item.label === "ホテル");
    const selectedHotel = listHotel?.options.find(
      (item) => item.slug === hotel_slug,
    );
    if (selectedHotel && !isTopPage) {
      reset({ ...initialFormData, hotelOrCity: selectedHotel });
    } else {
      reset({ ...initialFormData, hotelOrCity: null });
    }
  }, [pathName, japanAreas, listCityAndHotel]);

  React.useEffect(() => {
    const currentPromoCode = getValues("promoCode");
    reset({
      ...initialFormData,
      hotelOrCity: getValues("hotelOrCity"),
      promoCode: currentPromoCode ?? initialFormData.promoCode,
    });
    const { checkIn, checkOut } = checkAndValidateStoredDates(
      formData?.checkIn,
      DEFAULT_CHECKIN_DATE,
      formData?.checkout,
      DEFAULT_CHECKOUT_DATE,
    );
    if (
      formData?.checkIn &&
      formData?.checkout &&
      range?.from &&
      range?.to &&
      (range.from?.getTime() !== checkIn.getTime() ||
        range.to?.getTime() !== checkOut.getTime())
    ) {
      setRange({
        from: parseStoredDate(checkIn, DEFAULT_CHECKIN_DATE),
        to: parseStoredDate(checkOut, DEFAULT_CHECKOUT_DATE),
      });
    }
  }, [initialFormData, reset]);

  const hasInvalidChildAge = rooms?.some((room) =>
    room?.children?.some((child) => child?.age?.value === -1),
  );

  return (
    <div
      className={cn(
        "relative w-[790px] rounded-lg p-6 py-[24px] shadow-[0px_3px_10px_rgba(0,0,0,0.161)] before:absolute before:inset-0 before:-z-10 before:rounded-lg before:bg-white before:opacity-50 before:transition-opacity hover:before:opacity-75",
        phone && "pb-3",
        className,
      )}
      {...props}
    >
      <FormProvider {...methods}>
        <form className="flex w-full gap-3" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex w-full flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="relative flex flex-col gap-1">
                <div className="text-[13px] font-[var(--font-semibold)] text-[var(--text-base-primary)]">
                  エリア名／ホテル名
                </div>
                <Controller
                  name="hotelOrCity"
                  control={control}
                  render={({ field }) => (
                    <>
                      <CustomSelect<
                        UtilSelectOption,
                        false,
                        GroupBase<UtilSelectOption>
                      >
                        value={field.value}
                        options={listCityAndHotel}
                        onChange={(newValue) => {
                          field.onChange(newValue);
                          setFilterRoomModalState(
                            FilterRoomModalStateEnum.SUBMITED,
                          );
                          if (showValidationError) {
                            setShowValidationError(false);
                          }
                          // Priority order for adult count on dropdown change:
                          // 1. formData.rooms exists in localStorage → user previously
                          //    changed something → their count wins, do nothing.
                          // 2. newValue.initial_adult_count (per-hotel CMS field)
                          // 3. initial_adult_count prop (hotel detail page)
                          // 4. default_adult_count (global CMS config)
                          // 5. DEFAULT_ADULTS (hardcoded fallback)
                          const userHasSavedRooms =
                            !!formData?.rooms && formData.rooms.length > 0;

                          if (userHasSavedRooms) {
                            // User previously changed counts — keep formData.rooms as-is
                            setValue("rooms", formData!.rooms as TRoom[]);
                          } else if (newValue?.type === "hotel") {
                            const configuredCount =
                              Number(newValue?.initial_adult_count) ||
                              Number(initial_adult_count) ||
                              Number(default_adult_count) ||
                              Number(DEFAULT_ADULTS);
                            const currentRooms = deepCloneRoom(
                              [...(rooms as TRoom[])],
                            );
                            if (currentRooms[0]) {
                              currentRooms[0] = {
                                ...currentRooms[0],
                                adults: configuredCount,
                              };
                            }
                            setValue("rooms", currentRooms);
                          } else if (!userHasSavedRooms && newValue?.type === "city") {
                            const configuredCount =
                              Number(default_adult_count) ||
                              Number(DEFAULT_ADULTS);
                            const currentRooms = deepCloneRoom(
                              [...(rooms as TRoom[])],
                            );
                            if (currentRooms[0]) {
                              currentRooms[0] = {
                                ...currentRooms[0],
                                adults: configuredCount,
                              };
                            }
                            setValue("rooms", currentRooms);
                          }
                        }}
                        onFocus={() => {
                          if (showValidationError) {
                            setShowValidationError(false);
                          }
                        }}
                        onInputChange={setInputValue}
                        inputValue={inputValue}
                        showAllButton={inputValue?.length > 0}
                        onShowAll={() => {
                          setInputValue("");
                        }}
                        placeholder="エリア名／ホテル名を入力してください"
                        loadingMessage={Loading}
                        isLoading={loading}
                        error={formState.errors.hotelOrCity?.value?.message}
                        className="w-full"
                        size="md"
                        iconCustom={
                          <MapPin
                            strokeWidth={1}
                            width={18}
                            height={18}
                            className="text-[#7F8498]"
                          />
                        }
                        filterOption={(option, inputValue) => {
                          return cityAndHotelFuzzyFilter(
                            { label: option.label, data: option.data },
                            inputValue,
                          );
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            paddingLeft: "12px",
                            paddingRight: "12px",
                            padding: "8px",
                            fontSize: "13px",
                            borderRadius: "var(--radius-input-md)",
                            borderEndStartRadius:
                              field.value?.type === "hotel"
                                ? "0px"
                                : "var(--radius-input-md)",
                            borderEndEndRadius:
                              field.value?.type === "hotel"
                                ? "0px"
                                : "var(--radius-input-md)",
                            maxHeight: "43px",
                            cursor: "text",
                            "&:hover": {
                              borderColor:
                                showValidationError && !field.value
                                  ? "var(--fill-red-solid-normal)"
                                  : base.borderColor,
                            },
                            borderColor:
                              showValidationError && !field.value
                                ? "var(--fill-red-solid-normal)"
                                : base.borderColor,
                            borderWidth:
                              showValidationError && !field.value
                                ? "2px"
                                : "1px",
                          }),
                          placeholder: (base) => ({
                            ...base,
                            fontSize: "12px",
                            color: "var(--text-base-primary)",
                            opacity: 0.6,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }),
                          singleValue: (base) => ({
                            ...base,
                            fontSize: "13px",
                            color: "var(--text-gray-stronger)",
                          }),
                          option: (base) => ({
                            ...base,
                            fontSize: "13px",
                            color: "var(--text-gray-stronger)",
                            cursor: "pointer",
                          }),
                          groupHeading: (base) => ({
                            ...base,
                            fontSize: "14px",
                            fontWeight: "bold",
                            color: "var(--text-gray-stronger)",
                            padding: "8px 12px",
                          }),
                        }}
                      />
                      {field.value?.type === "hotel" &&
                        field.value?.slug != hotel_slug && (
                          <div className="absolute -bottom-6 z-10 w-full rounded-b-md border border-gray-300 bg-white p-1.5 shadow-md">
                            <Link
                              href={`${globalThis.location.origin}/${locale}/${japanAreas.flatMap((item) => item.hotels).find((item) => item?.item_name === field.value?.hotelItemName)?.slug}`}
                              target="_blank"
                              className="flex items-center justify-center text-xs font-semibold text-[#FFB100] hover:underline"
                            >
                              このホテルの写真・詳細を見る
                            </Link>
                          </div>
                        )}
                    </>
                  )}
                />
              </div>

              <div className="flex flex-col gap-1">
                <div className="text-[13px] font-[var(--font-semibold)] text-[var(--text-base-primary)]">
                  {checkin_label} - {checkout_label}
                </div>
                <DateRangePicker
                  range={range}
                  onChange={handleRangeSelect}
                  onCancel={() => {
                    setRange(previousRange);
                  }}
                >
                  <Button
                    hierarchy="outline"
                    variant="neutral"
                    type="button"
                    size={"xl"}
                    className="flex max-h-[43px] w-full items-center justify-around rounded-md border border-gray-300 px-3 py-2 text-[13px] font-medium text-[var(--text-base-primary)]"
                  >
                    <span>
                      {range?.from
                        ? format(range.from, datePickerFormatJP)
                        : ""}
                    </span>
                    <span className="mx-2">→</span>
                    <span>
                      {range?.to ? format(range.to, datePickerFormatJP) : ""}
                    </span>
                  </Button>
                </DateRangePicker>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-3 text-[var(--text-base-primary)] sm:grid-cols-2 md:grid-cols-5">
              <div className="flex flex-col">
                <div className="text-[13px] font-[var(--font-semibold)] text-[var(--text-base-primary)]">
                  {adult_label}
                </div>
                <div className="mt-auto mb-1 flex items-center">
                  <Button
                    hierarchy={canDecreaseAdults ? "filled" : "outline"}
                    type="button"
                    className="flex h-9 w-9 items-center justify-center rounded-md border-none"
                    onClick={decrementAdults}
                  >
                    <span className="text-xl">-</span>
                  </Button>
                  <div className="mx-2 w-5 text-center">{adultsCount}</div>
                  <Button
                    hierarchy={canIncreaseAdults ? "filled" : "outline"}
                    type="button"
                    className="flex h-9 w-9 items-center justify-center rounded-md border-none"
                    onClick={incrementAdults}
                  >
                    <span className="text-xl">+</span>
                  </Button>
                </div>
              </div>

              <div className="flex flex-col">
                <div className="text-[13px] font-[var(--font-semibold)] text-[var(--text-base-primary)]">
                  {children_label}
                </div>
                <div className="mt-auto mb-1 flex items-center">
                  <Button
                    hierarchy={canDecreaseChildren ? "filled" : "outline"}
                    type="button"
                    className="flex h-9 w-9 items-center justify-center rounded-md border-none"
                    onClick={decrementChildren}
                  >
                    <span className="text-xl">-</span>
                  </Button>
                  <div className="mx-2 w-5 text-center">{childrenCount}</div>
                  <Button
                    hierarchy={canIncreaseChildren ? "filled" : "outline"}
                    type="button"
                    className="flex h-9 w-9 items-center justify-center rounded-md border-none"
                    onClick={incrementChildren}
                  >
                    <span className="text-xl">+</span>
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <div className="text-[13px] font-[var(--font-semibold)] text-[var(--text-base-primary)]">
                  {single_room_label}
                </div>
                <div className="mt-auto">
                  <Controller
                    name="rooms"
                    control={control}
                    render={({ field }) => {
                      const roomCount = field.value?.length || 1;

                      return (
                        <>
                          <Button
                            variant="neutral"
                            hierarchy="outline"
                            size="xl"
                            type="button"
                            className="flex max-h-[43px] w-full items-center justify-between rounded-md border border-gray-300 px-3 py-2 text-[13px] text-[var(--text-base-primary)]"
                            onClick={toggleModal}
                          >
                            {`${roomCount} ${single_room_label || "部屋"}`}
                          </Button>

                          <Modal
                            open={isOpen}
                            toggleModal={toggleModal}
                            containerClassName="rounded-2xl !max-w-[450px] gap-0"
                            closeClassName="bg-[var(--fill-base-main)] text-white rounded-full opacity-100 p-1.5"
                            header={
                              <div className="border-b border-b-[var(--stroke-gray-light)] px-4 pt-4 pb-3 text-left text-xl leading-8 font-[var(--font-semibold)] text-[var(--text-gray-stronger)]">
                                部屋選択
                              </div>
                            }
                            footer={
                              <div className="w-full border-t border-t-[var(--stroke-gray-light)] px-4 pt-3 pb-4">
                                <Button
                                  variant="secondary"
                                  className="flex w-full items-center justify-between"
                                  size="lg"
                                  type="button"
                                  onClick={toggleModal}
                                  disabled={hasInvalidChildAge}
                                >
                                  <span>{tripla_apply_label}</span>
                                </Button>
                              </div>
                            }
                          >
                            <div className="max-h-[500px] overflow-y-auto px-4 pt-3 pb-4">
                              <RoomSelect
                                roomConfiguration={{
                                  adultLabel:
                                    tripla_room_adults_label ?? undefined,
                                  childrenLabel:
                                    tripla_room_children_label ?? undefined,
                                  addRoomButtonText:
                                    tripla_add_room_label ?? undefined,
                                  maximumAdults: String(MAXIMUM_ADULTS_LIMIT),
                                  maximumChildAge:
                                    maximum_child_age ?? undefined,
                                  maximumChildren: String(
                                    MAXIMUM_CHILDREN_LIMIT,
                                  ),
                                  maximumRooms: String(MAXIMUM_ROOMS_LIMIT),
                                  singleRoomLabel:
                                    tripla_room_name_label ?? undefined,
                                  bedLabel: tripla_bed_label ?? undefined,
                                  withBedLabel:
                                    tripla_with_bed_label ?? undefined,
                                  withoutBedLabel:
                                    tripla_without_bed_label ?? undefined,
                                  default_adult_count: String(adultsCount),
                                }}
                                AgesConfigurations={{
                                  agesLabel: tripla_age_label ?? undefined,
                                  agePlaceholder: age_placeholder ?? undefined,
                                  singleChildLabel:
                                    tripla_child_label ?? undefined,
                                  ageValuePlaceholder:
                                    tripla_age_placeholder ?? undefined,
                                  invalidAgeError:
                                    tripla_invalid_age_error ?? undefined,
                                }}
                              />
                            </div>
                          </Modal>
                        </>
                      );
                    }}
                  />
                </div>
              </div>

              <div className="col-span-1 flex flex-col gap-1 md:col-span-2">
                <div className="text-[13px] font-[var(--font-semibold)] text-[var(--text-base-primary)]">
                  {promo_code_label}
                </div>
                <Controller
                  name="promoCode"
                  control={control}
                  render={({ field }) => (
                    <Input
                      className="mt-auto max-h-[43px] w-full rounded-md border border-gray-300 bg-white py-[12px] text-[13px] text-[var(--text-base-primary)] placeholder:text-sm"
                      value={field.value || ""}
                      size={"lg"}
                      onChange={(e) => {
                        field.onChange(e);
                        setFilterRoomModalState(
                          FilterRoomModalStateEnum.SUBMITED,
                        );
                        setFormData({
                          ...(formData as SearchAvailableRoom),
                          promoCode: e.target.value,
                        });
                      }}
                      placeholder={promo_code_placeholder || ""}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="mt-auto flex justify-end">
            <Button
              variant="secondary"
              size="xl"
              type="submit"
              loading={isLoading}
              className="flex h-[43px] items-center justify-between gap-3 px-6 text-[13px]"
            >
              <span>{booking_button_text}</span>
              <CircleChevronRight strokeWidth="1px" width={18} height={18} />
            </Button>
          </div>
        </form>
      </FormProvider>
      {!isHiddenMobile && phone && (
        <div className="mt-2 flex items-center justify-center gap-1 text-[var(--text-base-primary)]">
          <Phone className="h-4 w-4" />

          {/* Clickable Phone */}
          <Link href={`tel:${phone}`} className="text-inherit no-underline">
            <span>{phone}</span>
          </Link>

          {/* Non-clickable Timings */}
          {timings && <span>（{timings}）</span>}
        </div>
      )}
    </div>
  );
};

export { SearchAvailableRoomForm };
